import React, { useEffect, useMemo, useState } from 'react';
import { api } from '../../middleware/api';
import { END_POINTS } from '../../utils/constants/endPoints';
import SuccessToast from '../../components/successToast';
import ValidationErrorToast from '../../components/validationErrorToast';
import InterviewSidebar from './InterviewSidebar';
import { styles } from './interviewStyles';

const PROFILE_KEY = 'mploi_interview_company_profile';
const QUESTION_SETS_KEY = 'mploi_interview_question_sets';
const DRAFTS_KEY = 'mploi_interview_invite_drafts';

interface InviteDraft {
  id: string;
  jobId: string;
  jobTitle: string;
  questionSetId: string;
  questionSetName: string;
  candidateEmails: string[];
  message: string;
  link: string;
  createdAt: string;
}

const fieldLabel: React.CSSProperties = { display: 'block', fontSize: '13px', fontWeight: 600, color: '#333', marginBottom: '6px' };

const getEmails = (value: string) => value
  .split(/[\n,;]/)
  .map((email) => email.trim())
  .filter(Boolean);

const InterviewInvites: React.FC = () => {
  const [jobs, setJobs] = useState<any[]>([]);
  const [questionSets, setQuestionSets] = useState<any[]>([]);
  const [profile, setProfile] = useState<any>({});
  const [drafts, setDrafts] = useState<InviteDraft[]>([]);
  const [jobId, setJobId] = useState('');
  const [questionSetId, setQuestionSetId] = useState('');
  const [candidateEmails, setCandidateEmails] = useState('');
  const [message, setMessage] = useState('');
  const [loading, setLoading] = useState(true);
  const [sending, setSending] = useState(false);

  useEffect(() => {
    (async () => {
      try {
        const [jobRes] = await Promise.all([api.get(END_POINTS.interviewJobs, {}).catch(() => [])]);
        setJobs(Array.isArray(jobRes) ? jobRes : (jobRes?.results || []));
      } catch (error: any) {
        ValidationErrorToast('Error', error?.message || 'Failed to load jobs');
      } finally {
        setLoading(false);
      }
    })();

    try {
      const savedProfile = localStorage.getItem(PROFILE_KEY);
      const savedSets = localStorage.getItem(QUESTION_SETS_KEY);
      const savedDrafts = localStorage.getItem(DRAFTS_KEY);
      if (savedProfile) setProfile(JSON.parse(savedProfile));
      if (savedSets) setQuestionSets(JSON.parse(savedSets));
      if (savedDrafts) setDrafts(JSON.parse(savedDrafts));
    } catch (error) {
      console.error('Failed to load invite workspace:', error);
    }
  }, []);

  useEffect(() => {
    if (!message && profile?.inviteMessage) setMessage(profile.inviteMessage);
  }, [profile, message]);

  const selectedJob = useMemo(() => jobs.find((job) => String(job.id) === String(jobId)), [jobs, jobId]);
  const selectedSet = useMemo(() => questionSets.find((set) => String(set.id) === String(questionSetId)), [questionSets, questionSetId]);
  const emails = useMemo(() => getEmails(candidateEmails), [candidateEmails]);

  const inviteLink = useMemo(() => {
    if (!selectedJob) return '';
    const token = selectedJob.publicToken || selectedJob.public_token || selectedJob.token || selectedJob.inviteToken || selectedJob.invite_token || selectedJob.id;
    return `${window.location.origin}/interview-simulator/job/${token}`;
  }, [selectedJob]);

  const persistDrafts = (nextDrafts: InviteDraft[]) => {
    setDrafts(nextDrafts);
    localStorage.setItem(DRAFTS_KEY, JSON.stringify(nextDrafts));
  };

  const saveDraft = () => {
    if (!selectedJob) return ValidationErrorToast('Required', 'Select a job first');
    if (emails.length === 0) return ValidationErrorToast('Required', 'Enter at least one candidate email');

    const draft: InviteDraft = {
      id: `${Date.now()}`,
      jobId: String(selectedJob.id),
      jobTitle: selectedJob.title || selectedJob.jobTitle || 'Interview',
      questionSetId: selectedSet?.id || '',
      questionSetName: selectedSet?.name || 'Default questions',
      candidateEmails: emails,
      message,
      link: inviteLink,
      createdAt: new Date().toISOString(),
    };
    persistDrafts([draft, ...drafts]);
    SuccessToast('Saved', 'Branded invite draft saved');
  };

  const sendInvites = async () => {
    if (!selectedJob) return ValidationErrorToast('Required', 'Select a job first');
    if (emails.length === 0) return ValidationErrorToast('Required', 'Enter at least one candidate email');
    try {
      setSending(true);
      const res: any = await api.post(`interviews/jobs/${selectedJob.id}/invites/send/`, {
        emails,
        message,
        inviteLink,
        companyName: profile?.companyName,
        senderName: profile?.senderName || profile?.contactName || profile?.companyName,
        replyTo: profile?.replyToEmail,
        // Visual branding so the sent email matches the Invite Preview card.
        logoUrl: profile?.logoUrl,
        brandColor: profile?.brandColor,
        questionsLabel: selectedSet?.name ? `${selectedSet.name}${selectedSet?.questions?.length ? ` (${selectedSet.questions.length})` : ''}` : '',
      });
      const data = res?.data ?? res ?? {};
      if (data.dryRun) {
        SuccessToast('Invites recorded', `${data.created || emails.length} invite(s) recorded — but email isn't switched on for this server yet, so nothing was sent.`);
      } else {
        SuccessToast('Invites sent', `${data.sent || 0} invite(s) emailed to your candidate${data.sent === 1 ? '' : 's'}.`);
      }
    } catch (e: any) {
      ValidationErrorToast('Could not send', e?.response?.data?.error || e?.message || 'Something went wrong sending the invites');
    } finally {
      setSending(false);
    }
  };

  const copyInvite = async () => {
    if (!selectedJob) return ValidationErrorToast('Required', 'Select a job first');
    const text = `${profile?.companyName || 'MPLOI'} Interview Invitation\n\n${message}\n\nRole: ${selectedJob.title || selectedJob.jobTitle || 'Interview'}\nQuestions: ${selectedSet?.name || 'Default question flow'}${selectedSet?.questions?.length ? ` (${selectedSet.questions.length} questions)` : ''}\n\nStart here: ${inviteLink}\n\nFrom: ${profile?.senderName || profile?.contactName || profile?.companyName || 'MPLOI'}${profile?.replyToEmail ? `\nReply-to: ${profile.replyToEmail}` : ''}`;
    await navigator.clipboard.writeText(text);
    SuccessToast('Copied', 'Invite copied to clipboard');
  };

  const deleteDraft = (id: string) => {
    persistDrafts(drafts.filter((draft) => draft.id !== id));
    SuccessToast('Deleted', 'Invite draft removed');
  };

  if (loading) {
    return (
      <InterviewSidebar>
        <div style={styles.emptyState}>
          <div className="spinner-border" role="status" style={{ color: '#0065FF' }}><span className="sr-only">Loading...</span></div>
          <p style={{ marginTop: '20px', ...styles.secondaryText }}>Loading invite builder...</p>
        </div>
      </InterviewSidebar>
    );
  }

  const companyName = profile?.companyName || 'Your Company';
  const brandColor = profile?.brandColor || '#0065FF';

  return (
    <InterviewSidebar>
      <div className="row">
        <div className="col-lg-6" style={{ marginBottom: '24px' }}>
          <div style={styles.card}>
            <h3 style={styles.cardTitle}>Branded Interview Invites</h3>
            <p style={{ ...styles.secondaryText, marginTop: '-8px' }}>Build candidate invites that present the interview as coming from the company, not generic MPLOI branding.</p>

            <label style={fieldLabel}>Job</label>
            <select style={styles.modalInput as any} value={jobId} onChange={(e) => setJobId(e.target.value)}>
              <option value="">Select a job</option>
              {jobs.map((job) => <option key={job.id} value={job.id}>{job.title || job.jobTitle || `Job ${job.id}`}</option>)}
            </select>

            <label style={{ ...fieldLabel, marginTop: '14px' }}>Question set</label>
            <select style={styles.modalInput as any} value={questionSetId} onChange={(e) => setQuestionSetId(e.target.value)}>
              <option value="">Default simulator questions</option>
              {questionSets.map((set) => <option key={set.id} value={set.id}>{set.name} ({set.questions?.length || 0})</option>)}
            </select>

            <label style={{ ...fieldLabel, marginTop: '14px' }}>Candidate emails</label>
            <textarea style={{ ...styles.modalInput, minHeight: '110px' } as any} value={candidateEmails} onChange={(e) => setCandidateEmails(e.target.value)} placeholder={'candidate@example.com\nanother@example.com'} />
            <div style={{ ...styles.secondaryText, fontSize: '12px', marginTop: '6px' }}>{emails.length} recipient{emails.length === 1 ? '' : 's'} detected.</div>

            <label style={{ ...fieldLabel, marginTop: '14px' }}>Invite message</label>
            <textarea style={{ ...styles.modalInput, minHeight: '140px' } as any} value={message} onChange={(e) => setMessage(e.target.value)} />

            <div style={styles.modalFooter}>
              <button onClick={copyInvite} style={{ ...styles.button, ...styles.secondaryButton } as any}>Copy Invite</button>
              <button onClick={saveDraft} style={{ ...styles.button, ...styles.secondaryButton } as any}>Save Draft</button>
              <button onClick={sendInvites} disabled={sending} style={{ ...styles.button, ...styles.primaryButton, ...(sending ? { opacity: 0.6 } : {}) } as any}>{sending ? 'Sending…' : 'Send Invites'}</button>
            </div>
          </div>
        </div>

        <div className="col-lg-6" style={{ marginBottom: '24px' }}>
          <div style={{ ...styles.card, marginBottom: '24px' }}>
            <h3 style={styles.cardTitle}>Invite Preview</h3>
            <div style={{ border: '1px solid #E0E0E0', borderRadius: '14px', overflow: 'hidden' }}>
              <div style={{ height: '8px', background: brandColor }} />
              <div style={{ padding: '24px' }}>
                {profile?.logoUrl && <img src={profile.logoUrl} alt={companyName} style={{ maxWidth: '150px', maxHeight: '64px', objectFit: 'contain', marginBottom: '16px' }} />}
                <h4 style={styles.primaryText}>{companyName} has invited you to interview</h4>
                <p style={{ ...styles.secondaryText, lineHeight: '1.7' }}>{message || 'Your invite message will appear here.'}</p>
                <div style={{ background: '#F8F9FA', padding: '14px', borderRadius: '10px', marginTop: '16px' }}>
                  <strong style={styles.primaryText}>{selectedJob?.title || selectedJob?.jobTitle || 'Select a job'}</strong>
                  <div style={{ ...styles.secondaryText, fontSize: '13px', marginTop: '4px' }}>Questions: {selectedSet?.name || 'Default simulator questions'}{selectedSet?.questions?.length ? ` (${selectedSet.questions.length})` : ''}</div>
                  {inviteLink && <div style={{ ...styles.secondaryText, fontSize: '13px', marginTop: '4px', wordBreak: 'break-all' as any }}>{inviteLink}</div>}
                </div>
                <button style={{ ...styles.button, backgroundColor: brandColor, color: '#fff', width: '100%', marginTop: '16px' } as any}>Start Interview</button>
                <p style={{ ...styles.secondaryText, fontSize: '12px', marginTop: '12px' }}>From: {profile?.senderName || profile?.contactName || companyName}{profile?.replyToEmail ? ` • Reply-to: ${profile.replyToEmail}` : ''}</p>
              </div>
            </div>
          </div>

          <div style={styles.card}>
            <h3 style={styles.cardTitle}>Saved Drafts ({drafts.length})</h3>
            {drafts.length === 0 ? <p style={styles.secondaryText}>No invite drafts saved yet.</p> : drafts.map((draft) => (
              <div key={draft.id} style={{ borderBottom: '1px solid #E0E0E0', padding: '12px 0' }}>
                <strong style={styles.primaryText}>{draft.jobTitle}</strong>
                <div style={{ ...styles.secondaryText, fontSize: '13px' }}>{draft.candidateEmails.length} recipient(s) • {draft.questionSetName} • {new Date(draft.createdAt).toLocaleString()}</div>
                <div style={{ display: 'flex', gap: '8px', marginTop: '8px', flexWrap: 'wrap' }}>
                  <button onClick={() => navigator.clipboard.writeText(draft.link)} style={{ ...styles.button, ...styles.secondaryButton, padding: '6px 10px', fontSize: '12px' } as any}>Copy Link</button>
                  <button onClick={() => deleteDraft(draft.id)} style={{ ...styles.button, ...styles.dangerButton, padding: '6px 10px', fontSize: '12px' } as any}>Delete</button>
                </div>
              </div>
            ))}
          </div>
        </div>
      </div>
    </InterviewSidebar>
  );
};

export default InterviewInvites;
